import { useCallback, useRef, useState } from "react";

import type { IEmployeeLocationFilterDto } from "app/pages/sales-intelligence/domain/contacts/types/filter-values";
import type { SalesloftCadence } from "app/pages/sales-intelligence/modules/salesloft/types/salesloft.types";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";

import { DEFAULT_PLAYBOOK_CONTACTS_COUNT, getPlaybookContactsCount, MAX_PLAYBOOK_CONTACTS_COUNT } from "../constants";
import { useCreatePlaybookMutation } from "../queries/useCreatePlaybookMutation";
import { useUpdatePlaybookMutation } from "../queries/useUpdatePlaybookMutation";
import type { EmailPersonalization, Playbook } from "../types/playbooks.types";
import type { PlaybookWizardMode } from "../types/playbookWizard.types";

import { usePlaybooksSalesloftReconnectHandler } from "./usePlaybooksSalesloftReconnectHandler";
import { usePlaybookToasts } from "./usePlaybookToasts";

type UsePlaybookWizardStateParams = {
    mode: PlaybookWizardMode;
    playbook?: Playbook;
    initialIsSharedWithAccount?: boolean;
};

export const usePlaybookWizardState = ({
    mode,
    playbook,
    initialIsSharedWithAccount = false,
}: UsePlaybookWizardStateParams) => {
    const { showSuccess, showError } = usePlaybookToasts();
    const handleCadenceReconnectSuccess = usePlaybooksSalesloftReconnectHandler();
    const canManageShared = salesSettingsHelper.isHighLevelAccount();

    const [name, setNameState] = useState(playbook?.name ?? "");
    const [nameTouched, setNameTouched] = useState(mode === "edit" || !!playbook?.name);
    const nameTouchedRef = useRef(nameTouched);
    nameTouchedRef.current = nameTouched;

    const setName = useCallback((value: string) => {
        nameTouchedRef.current = true;
        setNameTouched(true);
        setNameState(value);
    }, []);

    const prefillName = useCallback((value: string) => {
        if (!nameTouchedRef.current) {
            setNameState(value);
        }
    }, []);

    const [description, setDescription] = useState(playbook?.description ?? "");

    const [isSharedWithAccount, setIsSharedWithAccountState] = useState(
        playbook?.isSharedWithAccount ?? initialIsSharedWithAccount,
    );
    const [scoringRuleId, setScoringRuleId] = useState<string | null>(playbook?.scoringRuleId ?? null);
    const [selectedCadence, setSelectedCadence] = useState<SalesloftCadence | null>(playbook?.cadence ?? null);
    const [emailPersonalization, setEmailPersonalization] = useState<EmailPersonalization>("as-is");
    const [enabledProfileIds, setEnabledProfileIds] = useState<string[]>(playbook?.employeeProfilesIds ?? []);
    const [contactsCount, setContactsCount] = useState<number | undefined>(
        playbook?.contactsCount && playbook.contactsCount > 0 && playbook.contactsCount <= MAX_PLAYBOOK_CONTACTS_COUNT
            ? playbook.contactsCount
            : DEFAULT_PLAYBOOK_CONTACTS_COUNT,
    );
    const [employeeLocation, setEmployeeLocation] = useState<IEmployeeLocationFilterDto | undefined>(
        playbook?.employeeLocation,
    );

    const setIsSharedWithAccount = (value: boolean) => {
        setIsSharedWithAccountState((current) => {
            if (current !== value) {
                setScoringRuleId(null);
                setEnabledProfileIds([]);
            }

            return value;
        });
    };

    const selectedCadenceId = selectedCadence?.id ?? null;

    const { mutateAsync: createPlaybook, isPending: isCreating } = useCreatePlaybookMutation();
    const { mutateAsync: updatePlaybook, isPending: isUpdating } = useUpdatePlaybookMutation();
    const isSubmitting = isCreating || isUpdating;

    const toggleProfile = (profileId: string, enabled: boolean) => {
        setEnabledProfileIds((currentIds) =>
            enabled
                ? currentIds.includes(profileId)
                    ? currentIds
                    : [...currentIds, profileId]
                : currentIds.filter((id) => id !== profileId),
        );
    };

    const addProfile = (profileId: string) => {
        setEnabledProfileIds((currentIds) =>
            currentIds.includes(profileId) ? currentIds : [...currentIds, profileId],
        );
    };

    const removeProfile = (profileId: string) => {
        setEnabledProfileIds((currentIds) => currentIds.filter((id) => id !== profileId));
    };

    const submit = async (onSuccess: () => void) => {
        if (!scoringRuleId) {
            return;
        }

        const payload = {
            name: name.trim(),
            description: description.trim() || null,
            salesloftCadenceId: selectedCadenceId,
            employeeProfilesIds: enabledProfileIds,
            contactsCount: getPlaybookContactsCount(contactsCount),
            employeeLocation: employeeLocation ?? null,
            scoringRuleId,
            ...(canManageShared ? { isSharedWithAccount } : {}),
        };

        try {
            if (mode === "edit" && playbook) {
                await updatePlaybook({ playbookId: playbook.playbookId, payload });
                showSuccess("si.playbooks.wizard.toast.updated");
            } else {
                await createPlaybook(payload);
                showSuccess("si.playbooks.wizard.toast.created");
            }
            onSuccess();
        } catch (error) {
            sentryErrorTrackerWithScope({
                errorMessage: mode === "edit" ? "Failed to update playbook" : "Failed to create playbook",
                e: error,
                tags: { component: "playbooks" },
                params: {
                    extra: {
                        mode,
                        playbookId: playbook?.playbookId,
                    },
                },
            });
            showError();
        }
    };

    return {
        name,
        setName,
        prefillName,
        nameTouched,
        description,
        setDescription,
        isSharedWithAccount,
        setIsSharedWithAccount,
        scoringRuleId,
        setScoringRuleId,
        selectedCadence,
        setSelectedCadence,
        emailPersonalization,
        setEmailPersonalization,
        enabledProfileIds,
        contactsCount,
        setContactsCount,
        employeeLocation,
        setEmployeeLocation,
        selectedCadenceId,
        canManageShared,
        toggleProfile,
        addProfile,
        removeProfile,
        handleCadenceReconnectSuccess,
        submit,
        isSubmitting,
    };
};

export type PlaybookWizardState = ReturnType<typeof usePlaybookWizardState>;
